import type { ImageComponentData, VideoComponentData } from './../ricos-content';
import { unreachable } from './../ricos-content/ts-utils';
import type { PluginServices } from './../ricos-plugins/editorPlugins';
import { GalleryOptions_Layout_Type } from './../ricos-schema/next-schema';
import type { IMessage, PublicCommands } from './../ricos-types';
import { logInvariant } from './../_shared/log-invariant';
import type { PickMediaFn } from './../_shared/media/pick-media';
import type { PmGalleryNode } from './../_shared/pm-node-types';
import { upload } from './../_shared/upload/upload';
import { layoutRicosData } from './/layout-data-provider';

import { fileInputAccept } from './consts';
import type { GalleryPluginEditorConfig } from './types';
import { GALLERY_TYPE } from './types';

type FileState = { itemIndex: number };
type GalleryItem = NonNullable<PmGalleryNode['attrs']['items']>[0];

const getGalleryAttributes = ({
  uploadedData,
  componentData,
  fileState,
}: {
  uploadedData: ImageComponentData | VideoComponentData;
  componentData: PmGalleryNode['attrs'];
  fileState?: FileState;
}) => {
  const fileType = (uploadedData as VideoComponentData).thumbnail ? 'video' : 'image';

  let galleryItem: NonNullable<PmGalleryNode['attrs']['items']>[0];
  if (fileType === 'video') {
    galleryItem = galleryVideoBuilder(uploadedData as VideoComponentData);
  } else {
    galleryItem = galleryImageBuilder(uploadedData as ImageComponentData);
  }
  const itemPos: number | undefined = fileState?.itemIndex as number | undefined;
  const newComponentData = setItemInGallery(galleryItem, componentData, itemPos);
  return {
    ...newComponentData,
    error: null,
  };
};

const getGalleryErrorAttributes = (
  error: IMessage,
  componentData: PmGalleryNode['attrs'],
  fileState?: FileState
) => {
  const itemPos = fileState ? fileState.itemIndex : undefined;
  const galleryItem = {
    error,
    ...(itemPos !== undefined ? componentData.items?.[itemPos] ?? {} : {}),
  };
  const newComponentData = setItemInGallery(galleryItem as GalleryItem, componentData, itemPos);
  return {
    ...newComponentData,
  };
};

const setItemInGallery = (
  item: GalleryItem,
  componentData: PmGalleryNode['attrs'],
  itemPos?: number
): PmGalleryNode['attrs'] => {
  let { items } = componentData;
  if (typeof itemPos === 'undefined') {
    items = [...(items ?? []), item];
  } else {
    items = items ? [...items] : [];
    items[itemPos] = item;
  }
  return { ...componentData, items };
};

const galleryImageBuilder = (img: ImageComponentData) => {
  return {
    image: {
      media: {
        src: { url: img.file_name },
        height: img.height,
        width: img.width,
      },
    },
  };
};

const galleryVideoBuilder = (video: VideoComponentData) => {
  const {
    thumbnail: { pathname: url, width, height },
  } = video;
  return {
    video: {
      media: {
        src: { url: video.pathname },
        height: video.height || height,
        width: video.width || width,
      },
      thumbnail: {
        src: { url },
        width,
        height,
      },
    },
  };
};

const getGalleryFileType = (type: string) => {
  return type.match('image/*') ? 'image' : type.match('video/*') ? 'video' : '';
};

const getGalleryPickMedia = ({
  config,
  services,
}: {
  config?: GalleryPluginEditorConfig;
  services: Pick<PluginServices, 'devService' | 'uploadService'>;
}): PickMediaFn => {
  const { handleFileSelection, handleFileUpload } = config || {};
  const devPickMedia = services.devService().pickMedia;

  return devPickMedia
    ? devPickMedia
    : handleFileSelection
    ? async ({ multiple }) => {
        const data = await new Promise<(ImageComponentData | VideoComponentData)[]>(resolve =>
          handleFileSelection(
            undefined,
            multiple,
            ({ data }) => resolve(data),
            undefined,
            DEFAULT_DATA
          )
        );
        return { type: 'ready-media' as const, media: data instanceof Array ? data : [data] };
      }
    : async ({ mimeType, multiple }) => {
        const files =
          (await services.uploadService?.selectFiles({ accept: mimeType, multiple })) ?? [];
        return { type: 'file' as const, file: files, customUploadFn: handleFileUpload };
      };
};

const DEFAULT_DATA = {
  items: [],
  options: layoutRicosData[GalleryOptions_Layout_Type.GRID],
};

export const handleGalleryUpload = async ({
  publicCommands,
  services,
  config,
  nodeId,
  fileState,
}: {
  publicCommands: PublicCommands;
  services: Pick<PluginServices, 'devService' | 'uploadService'>;
  config?: GalleryPluginEditorConfig;
  nodeId?: string;
  fileState?: { itemIndex: number };
}) => {
  const { accept = fileInputAccept, multi = true, handleFileUpload } = config ?? {};
  const multiple = fileState ? false : multi;

  const pickMedia = getGalleryPickMedia({ config, services });

  const result = await pickMedia({ mimeType: accept, multiple });

  switch (result.type) {
    case 'ready-media': {
      const targetNodeId =
        nodeId ??
        publicCommands.insertBlockWithBlankLines(GALLERY_TYPE, DEFAULT_DATA, {
          updateSelection: true,
        });

      const rootItemsLength =
        (publicCommands?.getBlockComponentData(targetNodeId) as PmGalleryNode['attrs'])?.items
          ?.length || 0;

      result.media.forEach((item, index) => {
        setTimeout(() => {
          const componentData = publicCommands?.getBlockComponentData(
            targetNodeId
          ) as PmGalleryNode['attrs'];

          publicCommands.setBlock(
            targetNodeId,
            GALLERY_TYPE,
            getGalleryAttributes({
              uploadedData: item as ImageComponentData | VideoComponentData,
              componentData,
              fileState: fileState ? fileState : { itemIndex: rootItemsLength + index },
            })
          );
        });
      });
      return;
    }
    case 'file': {
      const files = result.file;

      const targetNodeId =
        nodeId ??
        publicCommands.insertBlockWithBlankLines(GALLERY_TYPE, DEFAULT_DATA, {
          updateSelection: true,
        });

      const rootItemsLength =
        (publicCommands?.getBlockComponentData(targetNodeId) as PmGalleryNode['attrs'])?.items
          ?.length || 0;

      await Promise.all(
        files.map(async (_file, index) => {
          const file = new File([_file], _file.name, { type: _file.type });
          const trackBi = services.uploadService.trackUploadBi(GALLERY_TYPE, file.size, file.name);
          const itemIndex = fileState ? fileState.itemIndex : rootItemsLength + index;
          try {
            const componentData = publicCommands?.getBlockComponentData(targetNodeId);

            const fileType = getGalleryFileType(file.type);
            const galleryItem = { [fileType]: { media: { src: { url: '' } } } };
            const newComponentData = setItemInGallery(galleryItem, componentData, itemIndex);

            publicCommands?.setBlock(targetNodeId, GALLERY_TYPE, {
              ...newComponentData,
              loading: true,
              error: null,
            });

            trackBi?.start();
            const uploadFn = result.customUploadFn || handleFileUpload;

            if (!uploadFn) {
              logInvariant(Error('No upload function provided'));
              throw new Error('No upload function provided');
            }

            const uploadedFile = await upload(uploadFn, file);

            publicCommands?.setBlock(targetNodeId, GALLERY_TYPE, {
              ...getGalleryAttributes({
                uploadedData: uploadedFile.data as ImageComponentData | VideoComponentData,
                componentData: publicCommands?.getBlockComponentData(
                  targetNodeId
                ) as PmGalleryNode['attrs'],
                fileState: { itemIndex },
              }),
            } satisfies PmGalleryNode['attrs']);

            trackBi?.success();
          } catch (error: any) {
            const componentData = publicCommands?.getBlockComponentData(targetNodeId);
            publicCommands?.setBlock(
              targetNodeId,
              GALLERY_TYPE,
              getGalleryErrorAttributes(error, componentData, { itemIndex })
            );

            trackBi?.error(error);
            services.uploadService.notifyError(error);
          }
        })
      );

      const componentData = publicCommands?.getBlockComponentData(targetNodeId);
      publicCommands?.setBlock(targetNodeId, GALLERY_TYPE, {
        ...componentData,
        loading: false,
      });

      return;
    }
    case 'cancel': {
      return;
    }
    default: {
      console.error(unreachable(result));
      return;
    }
  }
};
